import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, LogOut, Pencil, Trash2, Loader2, ExternalLink, Compass } from "lucide-react";
import { API } from "@/lib/brand";
import { useAuth } from "@/lib/auth";
import { categoryLabel, formatPublishedDate } from "@/lib/journal";

const STATUS_COLORS = {
  draft: "bg-paper-raised text-ink-soft border-line",
  published: "bg-brass/10 text-brass-deep border-brass/30",
  scheduled: "bg-pink/10 text-pink-deep border-pink/30",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/journal/admin/posts`, { credentials: "include" });
      if (res.ok) setPosts(await res.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This can't be undone.`)) return;
    const res = await fetch(`${API}/journal/admin/posts/${post.id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (res.ok) {
      toast.success("Post deleted");
      setPosts((p) => p.filter((x) => x.id !== post.id));
    } else {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-8 py-16">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="eyebrow mb-2">Journal · Admin</div>
          <h1 className="font-display text-3xl md:text-4xl text-ink leading-tight">
            Your posts
          </h1>
          {user && (
            <p className="text-ink-soft text-[13px] mt-2 num">Signed in as {user.email}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Link to="/journal/admin/compass" className="btn-outline" data-testid="view-compass">
            <Compass size={16} /> Compass requests
          </Link>
          <Link to="/journal/admin/new" className="btn-brass" data-testid="new-post">
            <Plus size={16} /> New post
          </Link>
          <button
            onClick={async () => { await logout(); window.location.href = "/journal"; }}
            className="btn-outline"
            data-testid="admin-logout"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex items-center justify-center text-ink-soft">
          <Loader2 size={22} className="animate-spin" />
        </div>
      ) : posts.length === 0 ? (
        <div className="card-paper p-10 text-center">
          <p className="text-ink-soft mb-4">No posts yet.</p>
          <Link to="/journal/admin/new" className="btn-brass"><Plus size={16} /> Write your first post</Link>
        </div>
      ) : (
        <div className="border hairline rounded-md overflow-hidden">
          <table className="w-full text-[14px]" data-testid="admin-posts-table">
            <thead>
              <tr className="border-b hairline bg-paper-raised text-left">
                <th className="eyebrow px-4 py-3">Title</th>
                <th className="eyebrow px-4 py-3">Category</th>
                <th className="eyebrow px-4 py-3">Status</th>
                <th className="eyebrow px-4 py-3">Updated</th>
                <th className="eyebrow px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id} className="border-b hairline last:border-b-0">
                  <td className="px-4 py-3 text-ink font-medium">
                    <div className="line-clamp-1">{p.title}</div>
                    <div className="num text-[11px] text-ink-soft mt-1">/{p.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-ink-soft">{categoryLabel(p.category)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] border ${STATUS_COLORS[p.status]}`}>
                      {p.status}
                      {p.status === "scheduled" && p.scheduled_for && (
                        <> · {formatPublishedDate(p.scheduled_for)}</>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 num text-ink-soft text-[12px]">
                    {formatPublishedDate(p.updated_at)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {p.status === "published" && (
                      <Link
                        to={`/journal/${p.slug}`}
                        className="inline-flex items-center gap-1 text-ink-soft hover:text-ink mx-1 text-[12px]"
                        title="View live"
                      >
                        <ExternalLink size={14} />
                      </Link>
                    )}
                    <Link
                      to={`/journal/admin/edit/${p.id}`}
                      className="inline-flex items-center gap-1 text-ink-soft hover:text-ink mx-1"
                      title="Edit"
                      data-testid={`edit-${p.slug}`}
                    >
                      <Pencil size={14} />
                    </Link>
                    <button
                      onClick={() => remove(p)}
                      className="inline-flex items-center gap-1 text-ink-soft hover:text-[#DC2626] mx-1"
                      title="Delete"
                      data-testid={`delete-${p.slug}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
